// Reading the guild off village/get: in a guild or not, your level and what it lets you do (the server's thresholds),
// the guild_details table, the no-guild lists and the world's caps. Pure functions over src/api/types.ts.
import type { GuildDetails, GuildMember, NoGuildInfo, Village } from "@/api/types";

/** GuildLevel 0 Warlord · 1 General · 2 Captain · 3 Warrior (the world process's ranks). */
export const LEVEL_NAMES = ["Warlord", "General", "Captain", "Warrior"] as const;
export const levelName = (level: number | null | undefined): string => LEVEL_NAMES[Math.max(0, Math.min(3, Number(level ?? 3)))] ?? "Warrior";

export const isGuildDetails = (gi: Village["guild_info"] | undefined): gi is GuildDetails => !!gi && typeof gi === "object" && "members" in gi && Array.isArray((gi as GuildDetails).members);
export const isNoGuild = (gi: Village["guild_info"] | undefined): gi is NoGuildInfo => !!gi && typeof gi === "object" && !("members" in gi) && ("invites" in gi || "requests" in gi);

/** The player's guild (null when not in one). Trusts guild_info over char_gid, which can lag by a read. */
export const guildOf = (v: Village | undefined): GuildDetails | null => (v && isGuildDetails(v.guild_info) && (v.guild_info.id ?? 0) > 0 ? v.guild_info : null);

export const noGuildOf = (v: Village | undefined): NoGuildInfo | null => (v && isNoGuild(v.guild_info) ? v.guild_info : null);

/** Your level in the guild: char_glevel, else your member row, else Warrior. */
export function myLevel(v: Village | undefined, uid: number | null): number {
  if (!v) return 3;
  if (typeof v.char_glevel === "number" && v.char_glevel >= 0) return v.char_glevel;
  const me = guildOf(v)?.members.find((m) => m.user_id === uid);
  return me ? Number(me.level) : 3;
}

/** The server's rules: only the Warlord sets levels; Warlord/General kick (someone below them), manage invites,
 *  requests and diplomacy. Greeting/announcement have no server check — the client keeps them to Warlord/General. */
export const can = {
  setLevel: (level: number) => level === 0,
  kick: (level: number, targetLevel: number) => level <= 1 && level < targetLevel,
  manageInvites: (level: number) => level <= 1,
  manageRequests: (level: number) => level <= 1,
  diplomacy: (level: number) => level <= 1,
  edit: (level: number) => level <= 1,
  disband: (level: number) => level === 0,
};

const numOf = (v: unknown): number | null => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") { const n = Number(v.replace(/[^\d.-]/g, "")); return Number.isFinite(n) ? n : null; }
  return null;
};

/** `guild_details` is a list of one-key objects: [{ Name }, { Tag }, { Members, is_members }, { Rank }, { "Total Points" }, …]. */
export function guildDetail(g: GuildDetails | null | undefined, key: string): number | null {
  if (!g?.guild_details) return null;
  for (const row of g.guild_details) if (row && typeof row === "object" && key in row) return numOf((row as Record<string, unknown>)[key]);
  return null;
}

/** The hero numbers, from guild_details when they are there, else from the member rows. */
export function guildStats(g: GuildDetails, points?: Map<number, { points: number; cities: number }>): { members: number; total: number; average: number; best: number; rank: number | null } {
  const members = g.members.length;
  const fromRows = g.members.map((m) => points?.get(m.user_id)?.points ?? 0);
  const rowsTotal = fromRows.reduce((a, b) => a + b, 0);
  const total = (points && rowsTotal > 0 ? rowsTotal : null) ?? guildDetail(g, "Total Points") ?? 0;
  const average = members ? Math.round(total / members) : 0;
  const best = (points && rowsTotal > 0 ? Math.max(0, ...fromRows) : null) ?? guildDetail(g, "Points of the Best Player") ?? 0;
  return { members, total, average, best, rank: guildDetail(g, "Rank") };
}

/** invite_list / request_list come as { "<user_id>": "<login>" } (or an empty array when PHP had nothing). */
export function idLogins(x: Record<string, string> | unknown[] | undefined): { user_id: number; login: string }[] {
  if (!x || Array.isArray(x)) return [];
  return Object.entries(x).map(([id, login]) => ({ user_id: Number(id), login: String(login) })).filter((r) => r.user_id > 0);
}

/** Members sorted by points (when known), then by level, then name. */
export function sortMembers(members: GuildMember[], points?: Map<number, { points: number; cities: number }>): GuildMember[] {
  return [...members].sort((a, b) => {
    const pa = points?.get(a.user_id)?.points ?? 0, pb = points?.get(b.user_id)?.points ?? 0;
    if (pb !== pa) return pb - pa;
    if (a.level !== b.level) return a.level - b.level;
    return a.login.localeCompare(b.login);
  });
}

/** The world's guild caps from village/get.config_limits (0 / missing = no cap). */
export function guildLimits(v: Village | undefined) {
  const c = v?.config_limits ?? {};
  const n = (k: string) => (typeof c[k] === "number" && c[k] > 0 ? c[k] : null);
  return {
    sendRequests: n("guild_max_send_requests_allowed"),
    receiveRequests: n("guild_max_receive_requests_allowed"),
    sendInvites: n("guild_max_send_invites_allowed"),
    receiveInvites: n("guild_max_receive_invites_allowed"),
  };
}

/** "25 members max" → 25 (the worlds overview's "Guild Member Limit"). */
export const memberLimit = (s: string | number | null | undefined): number | null => {
  if (typeof s === "number") return s > 0 ? s : null;
  const m = /(\d+)/.exec(String(s ?? ""));
  return m ? Number(m[1]) : null;
};

/** The client-side checks that mirror GuildController::createAction. */
export function validateGuildForm(name: string, tag: string, description: string): { name?: string; tag?: string; description?: string } {
  const e: { name?: string; tag?: string; description?: string } = {};
  if (name.trim().length < 1 || name.trim().length > 20) e.name = "Guild name must be between 1 and 20 characters";
  if (!/^[a-z0-9]{1,6}$/.test(tag.trim().toLowerCase())) e.tag = "Guild tag must be 1-6 characters long and contain only letters and numbers";
  if (description.trim().length < 1 || description.trim().length > 200) e.description = "Guild description must be between 1 and 200 characters";
  return e;
}

export const DIPLOMACY_KINDS = [
  { type: "a" as const, label: "Allies", one: "Ally", color: "var(--green)", quip: "An alliance is a pact of trust… and mutual blackmail." },
  { type: "n" as const, label: "Non-aggression pacts", one: "Pact", color: "#B8892F", quip: "A promise not to strike — kept until it isn't." },
  { type: "e" as const, label: "Enemies", one: "Enemy", color: "var(--crim-hi)", quip: "Marked crimson on the map. Strike freely." },
];
export type DiplomacyType = (typeof DIPLOMACY_KINDS)[number]["type"];
